/**
 * Demo v2 · Lane 1 "TRY IT NOW" — sector picker + live scenario.
 *
 * Single-page experience with three inline steps:
 *
 *   Step 1 · Pick your sector (5 super institutions)
 *   Step 2 · Preview the scenario for that sector
 *   Step 3 · Scan the QR with your phone → watch the laptop update
 *            in real time as the phone user approves or denies.
 *
 * The scenario preset (purpose, claims, plain-language outcome) is
 * loaded from `/api/demo/scenarios`. When the evaluator starts a
 * session, `/api/demo/showcase/session` mints a 60-min ephemeral
 * demo tenant and a scoped consent row, then this page polls
 * `/api/demo/showcase/session/{consent_id}` every 2s so the moment
 * the phone taps Approve, the laptop screen flips to the "what just
 * happened" narrative.
 */
import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle2, ShieldX, Smartphone, RefreshCw, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import VeraLokLogo from "@/marketing/VeraLokLogo";

const SECTORS = [
  "Hospitality", "Enterprise Services", "Health", "Finance", "Government",
];

export default function DemoTry() {
  const [sector, setSector] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const [catalogue, setCatalogue] = useState({});

  useEffect(() => {
    api.get("/demo/scenarios")
      .then((r) => {
        const map = {};
        (r.data?.sectors || []).forEach((s) => { map[s.sector] = s; });
        setCatalogue(map);
      })
      .catch(() => setCatalogue({}));
  }, []);

  const pickSector = (s) => {
    setSector(s);
    setScenario(catalogue[s] || null);
  };

  const start = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/demo/showcase/session", { sector });
      setSession(data);
      setScenario(data.scenario);
      setStatus({ state: "PENDING" });
    } catch (e) {
      setError(e?.response?.data?.detail?.reason_code || "start_failed");
    } finally {
      setBusy(false);
    }
  };

  // Poll the showcase status once a session exists
  useEffect(() => {
    if (!session?.consent_id) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(
          `/demo/showcase/session/${session.consent_id}`);
        if (!alive) return;
        setStatus(data);
        if (data.state !== "PENDING") {
          clearInterval(pollRef.current);
        }
      } catch {
        /* transient */
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(pollRef.current); };
  }, [session?.consent_id]);

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setSector(null); setScenario(null); setSession(null); setStatus(null);
  };

  return (
    <div className="vlk-demo-try">
      <div className="vlk-demo-try-frame">
        <header className="vlk-demo-try-header">
          <Link to="/demo" className="vlk-demo-try-back" data-testid="demo-try-back">
            <ArrowLeft size={14} /> Back
          </Link>
          <VeraLokLogo size={22} />
          <span className="vlk-demo-try-brand">VeraLok</span>
          <span className="vlk-demo-try-badge">DEMO V2</span>
        </header>

        {!session && (
          <SectorPicker
            sector={sector} onPick={pickSector}
            scenario={scenario} onStart={start} busy={busy} error={error}
          />
        )}
        {session && (
          <ActiveScenario
            session={session} scenario={scenario} status={status}
            onReset={reset}
          />
        )}
      </div>
    </div>
  );
}

function SectorPicker({ sector, onPick, scenario, onStart, busy, error }) {
  return (
    <div className="vlk-demo-try-picker" data-testid="demo-try-picker">
      <h1>Pick your sector.</h1>
      <p>We'll walk you through a real scenario that fits how your
        institution actually uses VeraLok.</p>
      <div className="vlk-demo-try-sectors">
        {SECTORS.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            data-testid={`demo-try-sector-${s.toLowerCase().replace(/\s+/g, '-')}`}
            className={`vlk-demo-try-sector${sector === s ? " on" : ""}`}
          >
            {s}
          </button>
        ))}
      </div>
      {scenario && (
        <div className="vlk-demo-try-preview" data-testid="demo-try-preview">
          <div className="vlk-demo-try-preview-title">{scenario.title}</div>
          <p>{scenario.narrative}</p>
          <button onClick={onStart} disabled={busy}
                  className="vlk-demo-try-start"
                  data-testid="demo-try-start">
            {busy ? "Preparing..." : "Start scenario"}
          </button>
        </div>
      )}
      {error && (
        <div className="text-sm text-rose-400 mt-2">
          Could not start: {error}
        </div>
      )}
    </div>
  );
}

function ActiveScenario({ session, scenario, status, onReset }) {
  const decided = status && status.state !== "PENDING";
  const granted = status && status.state === "GRANTED";
  const citizenUrl = `${window.location.origin}${session.citizen_url}`;
  return (
    <div className="vlk-demo-try-active" data-testid="demo-try-active">
      <div className="vlk-demo-try-active-left">
        <h1>{scenario.title}</h1>
        <p className="vlk-demo-try-active-narrative">{scenario.narrative}</p>
        <div className="vlk-demo-try-req">
          <div className="vlk-demo-try-req-row">
            <span>Purpose</span><b>{scenario.purpose}</b>
          </div>
          <div className="vlk-demo-try-req-row">
            <span>Claims requested</span>
            <b>{scenario.claims.join(", ")}</b>
          </div>
          <div className="vlk-demo-try-req-row">
            <span>Participant</span>
            <b>{session.citizen?.full_name || "Demo participant"}</b>
          </div>
        </div>
        {!decided && (
          <StatusPending />
        )}
        {decided && (
          <StatusOutcome scenario={scenario} granted={granted}
                         status={status} onReset={onReset} />
        )}
      </div>
      <div className="vlk-demo-try-active-right">
        <div className="vlk-demo-try-qr-caption">
          <Smartphone size={16} />
          <span>Scan with any phone · Demo v2 participant view</span>
        </div>
        <div className="vlk-demo-try-qr" data-testid="demo-try-qr">
          <QRCodeSVG value={citizenUrl}
                     size={220} bgColor="#ffffff" fgColor="#010F23"
                     includeMargin />
        </div>
        <a href={citizenUrl} target="_blank" rel="noreferrer"
           className="vlk-demo-try-qr-link"
           data-testid="demo-try-open-mobile">
          Or open the phone view here
        </a>
      </div>
    </div>
  );
}

function StatusPending() {
  return (
    <div className="vlk-demo-try-status pending" data-testid="demo-try-status-pending">
      <RefreshCw size={16} className="spin" />
      <span>Waiting for the participant to approve on their phone…</span>
    </div>
  );
}

function StatusOutcome({ scenario, granted, status, onReset }) {
  const Icon = granted ? CheckCircle2 : ShieldX;
  return (
    <div className={`vlk-demo-try-outcome ${granted ? "granted" : "denied"}`}
         data-testid="demo-try-outcome">
      <Icon className="vlk-demo-try-outcome-icon" size={22} />
      <div className="vlk-demo-try-outcome-headline">
        {granted ? scenario.outcome_headline : "The participant chose not to share."}
      </div>
      <p className="vlk-demo-try-outcome-body">
        {granted
          ? scenario.outcome_body
          : "The participant retains full control at every step. VeraLok never releases anything without explicit consent."}
      </p>
      <div className="vlk-demo-try-outcome-what">
        <div className="vlk-demo-try-outcome-what-title">
          What just happened
        </div>
        <ul>
          <li>You asked for exactly what your workflow needs — nothing more.</li>
          <li>The participant decided on their own device.</li>
          <li>You received a signed, timestamped result you can rely on.</li>
          <li>Every step is in the Demo v2 audit ledger.</li>
        </ul>
      </div>
      <button onClick={onReset} className="vlk-demo-try-again"
              data-testid="demo-try-again">
        Try another scenario
      </button>
    </div>
  );
}
